import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatAuthError, supabase } from '@/lib/supabase';
import { track, identifyUser } from '@/lib/posthog';
import { Loader2, Info } from 'lucide-react';

type SignupState = 'email' | 'otp' | 'redirecting' | 'checking';
type HintType = 'new' | 'unconfirmed' | null;

// Generate a random secure password that the user never needs to know
// They'll use OTP/magic link for all future logins
const generateRandomPassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 32; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

export function SignupForm() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';
  const hint = searchParams.get('hint') as HintType;

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [state, setState] = useState<SignupState>('checking');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Get hint message based on URL param
  const getHintMessage = () => {
    switch (hint) {
      case 'new':
        return 'No account found with this email. Create one below.';
      case 'unconfirmed':
        return "Your signup isn't complete yet. Verify your email to continue.";
      default:
        return null;
    }
  };
  const hintMessage = getHintMessage();

  // If the user is already authenticated, send them straight into the app.
  // Subscriptions are workspace-scoped now — there's no account-level
  // subscription gate, so a logged-in user without a Pro workspace just
  // proceeds normally and either creates/joins a workspace or is taken to
  // their existing one. The post-auth routes (WorkspaceGuard, etc.) handle
  // workspace selection.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          navigate('/home');
        } else {
          setState('email');
        }
      } catch (err) {
        console.error('Error checking auth state:', err);
        setState('email');
      }
    };
    checkAuth();
  }, [navigate]);

  const checkUserStatus = async (email: string): Promise<{ exists: boolean; confirmed: boolean }> => {
    try {
      const response = await supabase.functions.invoke('check-user-exists', {
        body: { email },
      });
      return {
        exists: response.data?.exists ?? false,
        confirmed: response.data?.confirmed ?? false,
      };
    } catch (err) {
      console.error('Error checking user status:', err);
      return { exists: false, confirmed: false };
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Check if user exists and if their email is confirmed
      const { exists, confirmed } = await checkUserStatus(email);

      // Redirect to login if user exists AND email is confirmed
      if (exists && confirmed) {
        navigate(`/login?email=${encodeURIComponent(email)}&hint=existing`);
        return;
      }

      // User doesn't exist OR exists but unconfirmed - create/resend OTP
      // For unconfirmed users, signUp will resend the confirmation email
      const { error: signupError } = await supabase.auth.signUp({
        email,
        password: generateRandomPassword(),
        options: {
          emailRedirectTo: `${window.location.origin}/checkout-success`,
        },
      });

      if (signupError) {
        console.error('Signup error:', signupError);
        throw signupError;
      }

      setState('otp');
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error: verifyError, data: verifyData } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup',
      });

      if (verifyError) throw verifyError;

      console.log('OTP verified, session data:', verifyData);

      // Give the client a moment to update its internal state
      await new Promise(resolve => setTimeout(resolve, 100));

      // Verify session is available
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session after OTP:', session ? 'exists' : 'null');

      if (!session) {
        throw new Error('Session not established after verification');
      }

      identifyUser(email, { email });
      track('signup_email_verified', { email });

      // Subscriptions are workspace-scoped, so we don't redirect to
      // Stripe Checkout here — there's no workspace yet. Send the new
      // user through onboarding (collect display name, then workspace
      // creation, which triggers checkout for the brand-new workspace).
      navigate('/onboarding');
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Checking auth state
  if (state === 'checking') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 text-teal-500 animate-spin" />
            <p className="text-sm text-gray-600">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }


  // Redirecting to Stripe state
  if (state === 'redirecting') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 text-teal-500 animate-spin" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Setting up your trial...
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Redirecting to secure checkout
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // OTP verification state
  if (state === 'otp') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">
            Check Your Email
          </CardTitle>
          <CardDescription>
            We sent a verification code to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="otp" className="text-sm font-medium">
                Verification Code
              </label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg tracking-widest"
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || otp.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Continue'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setState('email');
                setOtp('');
                setError('');
              }}
            >
              Use a different email
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Email entry state
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-teal-600">
          Start Your Free Trial
        </CardTitle>
        <CardDescription>
          Enter your email to get started with Ingesta
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Hint banner when redirected from login */}
        {hintMessage && (
          <div className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
            hint === 'unconfirmed'
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <Info className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
              hint === 'unconfirmed' ? 'text-amber-600' : 'text-blue-600'
            }`} />
            <p className={`text-sm ${
              hint === 'unconfirmed' ? 'text-amber-700' : 'text-blue-700'
            }`}>
              {hintMessage}
            </p>
          </div>
        )}

        <form onSubmit={handleSendCode} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus={!initialEmail}
            />
          </div>
          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Sending Code...' : 'Sign Up'}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-teal-600 hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
